// The incident banner state is served as a static file from the same origin as
// the SPA, so it stays reachable when AKS and APIM are down — which is exactly
// when the banner matters. Never route this through the API.
//
// Do not add a cache-busting query string: the Front Door route is configured
// IgnoreQueryString, so the edge would ignore it. Freshness comes from the
// cache rule in xplore-infra instead.

export const INCIDENT_URL = "/incident.json";
export const POLL_INTERVAL_MS = 60000;

const initialState = { active: false, dismissed: false };

let state = initialState;
let listeners = new Set();
let timer = null;
let started = false;

const notify = () => listeners.forEach((listener) => listener(state));

export const getState = () => state;

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const applyActive = (active) => {
  // A fresh incident clears a dismissal from the previous one.
  const dismissed = active && !state.active ? false : state.dismissed;
  state = { active, dismissed };
  notify();
};

export const dismiss = () => {
  state = { ...state, dismissed: true };
  notify();
};

export const fetchOnce = async () => {
  try {
    const response = await fetch(INCIDENT_URL, { cache: "no-store" });
    if (!response.ok) return applyActive(false);
    const body = await response.json();
    applyActive(body?.active === true);
  } catch {
    applyActive(false);
  }
};

const startTimer = () => {
  if (timer !== null) return;
  timer = setInterval(fetchOnce, POLL_INTERVAL_MS);
};

const stopTimer = () => {
  if (timer === null) return;
  clearInterval(timer);
  timer = null;
};

// A hidden tab cannot show a banner, so polling it only burns requests. Coming
// back to the tab refetches immediately rather than waiting out the interval.
const onVisibilityChange = () => {
  if (document.visibilityState === "hidden") return stopTimer();
  fetchOnce();
  startTimer();
};

const onFocus = () => fetchOnce();

export const startPolling = () => {
  if (started) return;
  started = true;
  globalThis.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibilityChange);
  fetchOnce();
  startTimer();
};

export const stopPolling = () => {
  stopTimer();
  if (!started) return;
  started = false;
  globalThis.removeEventListener("focus", onFocus);
  document.removeEventListener("visibilitychange", onVisibilityChange);
};

export const resetForTests = () => {
  stopPolling();
  listeners = new Set();
  state = initialState;
};
